import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadPDF, printThermal } from "../utils/exportHelpers";
import { inventoryApi } from "../utils/inventoryApi";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const InventoryStockSummaryReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [date, setDate] = useState(today());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [report, setReport] = useState({ items: [], totals: {} });
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (userId) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, date]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;
    opts.refresh ? setRefreshing(true) : setLoading(true);
    try {
      const data = await inventoryApi.getStockSummaryReport({
        restaurantid: userId,
        date,
      });
      setReport({
        items: Array.isArray(data?.items) ? data.items : [],
        totals: data?.totals || {},
      });
    } catch (e) {
      toast.error(e.message || "Unable to load stock summary report");
      setReport({ items: [], totals: {} });
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const fmtMoney = (n) =>
    `Rs ${Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const exportCsv = () => {
    const rows = report.items.map((row, index) => [
      index + 1,
      row.name || "-",
      row.base_uom || "",
      row.opening ?? 0,
      row.in ?? 0,
      row.used ?? 0,
      row.closing ?? 0,
      row.available ?? 0,
      row.min_stock ?? 0,
      row.used_value ?? 0,
    ]);
    downloadCSV(
      `inventory_stock_summary_${date}.csv`,
      [
        "#",
        "Item",
        "UOM",
        "Opening",
        "Stock In",
        "Used",
        "Closing",
        "Available",
        "Min Stock",
        "Used Value",
      ],
      rows
    );
  };

  const exportPdf = () =>
    downloadPDF(reportRef.current, `inventory_stock_summary_${date}.pdf`);

  const totals = report?.totals || {};

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Inventory Stock Summary</Title>
          <Sub>Opening, stock-in, usage and closing inventory snapshot by date.</Sub>
          <PillRow>
            <Pill>Opening: {totals.opening ?? 0}</Pill>
            <Pill>Stock In: {totals.in ?? 0}</Pill>
            <Pill>Used: {totals.used ?? 0}</Pill>
            <Pill>Closing: {totals.closing ?? 0}</Pill>
            <Pill>Used Value: {fmtMoney(totals.used_value)}</Pill>
          </PillRow>
        </div>
        <Actions>
          <DateInput>
            Date:
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </DateInput>
          <PrimaryBtn type="button" onClick={() => fetchReport({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </Top>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading report...</Center>
        ) : report.items.length === 0 ? (
          <Center>No data found for selected date.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th>UOM</th>
                  <th>Opening</th>
                  <th>In</th>
                  <th>Used</th>
                  <th>Closing</th>
                  <th>Available</th>
                  <th>Min Stock</th>
                  <th>Used Value</th>
                </tr>
              </thead>
              <tbody>
                {report.items.map((item, index) => (
                  <tr key={`${item.id || item.name || "row"}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{item.name || "-"}</td>
                    <td>{item.base_uom || "-"}</td>
                    <td>{item.opening ?? 0}</td>
                    <td>{item.in ?? 0}</td>
                    <td>{item.used ?? 0}</td>
                    <td>{item.closing ?? 0}</td>
                    <td>{item.available ?? 0}</td>
                    <td>{item.min_stock ?? 0}</td>
                    <td>{fmtMoney(item.used_value)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
        <Actions style={{ marginTop: 10 }}>
          <PrimaryBtn type="button" onClick={exportPdf}>
            PDF
          </PrimaryBtn>
          <PrimaryBtn
            type="button"
            onClick={() => printThermal(reportRef.current, `inventory_stock_summary_${date}`)}
          >
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={exportCsv}>
            CSV
          </PrimaryBtn>
        </Actions>
      </Card>
    </Page>
  );
};

export default InventoryStockSummaryReportPage;

const today = () => new Date().toISOString().slice(0, 10);

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;

const PillRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Pill = styled.span`
  background: #eef2ff;
  color: ${COLOR.text};
  border: 1px solid ${COLOR.line};
  border-radius: 12px;
  padding: 8px 10px;
  font-weight: 800;
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;

const Sub = styled.div`
  color: ${COLOR.muted};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};
  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;

const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  align-items: center;
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
  th,
  td {
    padding: 10px 12px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
    font-size: 14px;
  }
  th {
    background: #f8fafc;
    font-weight: 800;
  }

  @media (max-width: 768px) {
    min-width: 640px;
    th,
    td {
      padding: 8px 10px;
      font-size: 12px;
      white-space: nowrap;
    }
  }
`;

const Center = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 24px;
`;
